import React, { useEffect } from 'react';
import { StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withSpring, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Warning from '@/assets/icons/alertTriangleWhite';
import Success from '@/assets/icons/checkmark-thin';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';

// Dynamic Island height (~37) + small padding (8)
const DYNAMIC_ISLAND_OFFSET = 45;
// Regular status bar + small padding
const REGULAR_STATUS_BAR_OFFSET = 25;
const EXIT_DURATION = 300;

export const toastTypes = {
    SUCCESS: 'success',
    WARNING: 'warning',
} as const;

export const toastPositions = {
    TOP: 'top',
    BOTTOM: 'bottom',
} as const;

export type ToastPosition = (typeof toastPositions)[keyof typeof toastPositions];
export type ToastType = (typeof toastTypes)[keyof typeof toastTypes];

export type ToastProps = {
    message: string;
    toastType?: ToastType;
    positionY?: number;
    position?: ToastPosition;
    onPress?: () => void;
};

const styles = StyleSheet.create({
    commonToastStyle: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderRadius: 10,
        paddingVertical: 12,
        paddingHorizontal: 16,
        elevation: 4,
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        position: 'absolute',
        ...common.zIndex100,
        alignSelf: 'center',
    },
    message: {
        marginLeft: 8,
        color: ds.colorValue.text.primary,
    },
});

type AnimatedToastProps = ToastProps & {
    visible: boolean;
    onHidden: () => void;
};

export const Toast = ({
    message,
    toastType,
    positionY = 46,
    position = toastPositions.BOTTOM,
    onPress,
    visible,
    onHidden,
}: AnimatedToastProps): React.JSX.Element => {
    const { top: safeAreaInsetTop } = useSafeAreaInsets();
    const { height: windowHeight } = useWindowDimensions();
    const isTopPosition = position === toastPositions.TOP;
    const translateY = useSharedValue(isTopPosition ? -100 : 100);

    // Calculate the top position offset when position is TOP
    const topPositionY = isTopPosition
        ? safeAreaInsetTop + DYNAMIC_ISLAND_OFFSET + REGULAR_STATUS_BAR_OFFSET
        : positionY;

    useEffect(() => {
        if (visible) {
            translateY.value = withSpring(0, {
                damping: 15,
                stiffness: 120,
            });
            return;
        }
        const hiddenY = isTopPosition ? -windowHeight : windowHeight;
        translateY.value = withTiming(hiddenY, { duration: EXIT_DURATION }, finished => {
            if (finished) {
                runOnJS(onHidden)();
            }
        });
    }, [visible, isTopPosition, windowHeight, onHidden, translateY]);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: translateY.value }],
            ...(isTopPosition ? { top: topPositionY } : { bottom: positionY }),
        };
    });

    const renderIcon = () => {
        switch (toastType) {
            case toastTypes.WARNING:
                return <Warning color={ds.colorValue.icon.primary} />;
            default:
                return <Success color={ds.colorValue.icon.primary} />;
        }
    };

    return (
        <Animated.View style={[styles.commonToastStyle, animatedStyle]}>
            <TouchableOpacity onPress={onPress} disabled={!onPress}>
                <Row alignItems="center" justifyContent="center">
                    {renderIcon()}
                    <Text variant="bodySmall" style={styles.message}>
                        {message}
                    </Text>
                </Row>
            </TouchableOpacity>
        </Animated.View>
    );
};
